const SUPPORT_AI_OPERATIONS_READ = 'support.ai.operations.read' as const;
const SUPPORT_AI_RELEASE_REQUEST = 'support.ai.release.request' as const;
const SUPPORT_AI_REPLAY_EXECUTION = 'support.ai.evaluation.replay' as const;
const SUPPORT_AI_CUSTOMER_READINESS = 'support.ai.customer_readiness.review' as const;
const SUPPORT_AI_ROLLOUT_CONTROL = 'support.ai.rollout.control' as const;
const SUPPORT_AI_CALIBRATION_REVIEW = 'support.ai.calibration.review' as const;
const SUPPORT_AI_CALIBRATION_MANAGE = 'support.ai.calibration.manage' as const;
const SUPPORT_AI_CALIBRATION_APPROVE = 'support.ai.calibration.approve' as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isSafeRecord(value: unknown): value is Record<string, unknown> {
  try {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
  } catch {
    return false;
  }
}

const INVALID_DATA_SNAPSHOT = Symbol('invalid-data-snapshot');

function snapshotDataGraph(
  value: unknown,
  ancestors = new Set<object>()
): unknown | typeof INVALID_DATA_SNAPSHOT {
  if (value === null) return null;
  if (typeof value !== 'object') {
    return typeof value === 'function' || typeof value === 'symbol'
      ? INVALID_DATA_SNAPSHOT
      : value;
  }

  const objectValue = value as object;
  let tracked = false;
  try {
    if (Array.isArray(objectValue)) return INVALID_DATA_SNAPSHOT;
    const prototype = Object.getPrototypeOf(objectValue);
    if (prototype !== Object.prototype && prototype !== null) return INVALID_DATA_SNAPSHOT;
    if (ancestors.has(objectValue)) return INVALID_DATA_SNAPSHOT;

    const keys = Reflect.ownKeys(objectValue);
    const snapshot = Object.create(null) as Record<string, unknown>;
    ancestors.add(objectValue);
    tracked = true;
    for (const key of keys) {
      if (typeof key !== 'string') return INVALID_DATA_SNAPSHOT;
      const descriptor = Object.getOwnPropertyDescriptor(objectValue, key);
      if (!descriptor || descriptor.enumerable !== true || !('value' in descriptor)) {
        return INVALID_DATA_SNAPSHOT;
      }
      const nestedSnapshot = snapshotDataGraph(descriptor.value, ancestors);
      if (nestedSnapshot === INVALID_DATA_SNAPSHOT) return INVALID_DATA_SNAPSHOT;
      Object.defineProperty(snapshot, key, {
        configurable: false,
        enumerable: true,
        value: nestedSnapshot,
        writable: false
      });
    }
    return snapshot;
  } catch {
    return INVALID_DATA_SNAPSHOT;
  } finally {
    if (tracked) ancestors.delete(objectValue);
  }
}

function ownDataProperty(value: Record<string, unknown>, key: string): unknown {
  try {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    return descriptor && descriptor.enumerable === true && 'value' in descriptor
      ? descriptor.value
      : undefined;
  } catch {
    return undefined;
  }
}

function hasCapability(
  value: unknown,
  codename: string,
  riskLevel: Api.Support.SupportAIGovernanceRiskLevel,
  stepUpRequired: boolean
): boolean {
  const snapshot = snapshotDataGraph(value);
  if (snapshot === INVALID_DATA_SNAPSHOT || !isSafeRecord(snapshot) || ownDataProperty(snapshot, 'available') !== true) return false;
  const capabilities = ownDataProperty(snapshot, 'capabilities');
  if (!isSafeRecord(capabilities)) return false;
  const capability = ownDataProperty(capabilities, codename);
  return isSafeRecord(capability) && ownDataProperty(capability, 'granted') === true &&
    ownDataProperty(capability, 'risk_level') === riskLevel &&
    ownDataProperty(capability, 'step_up_required') === stepUpRequired;
}

/**
 * Project the one capability needed by the Operations workspace.
 * Missing, stale, unavailable, or malformed context is always denied.
 */
export function hasSupportAIOperationsAccess(value: unknown): boolean {
  if (!isSafeRecord(value) || ownDataProperty(value, 'available') !== true) return false;
  const capabilities = ownDataProperty(value, 'capabilities');
  if (!isSafeRecord(capabilities)) return false;
  const capability = ownDataProperty(capabilities, SUPPORT_AI_OPERATIONS_READ);
  return (
    isSafeRecord(capability) &&
    ownDataProperty(capability, 'granted') === true &&
    ownDataProperty(capability, 'risk_level') === 'read' &&
    ownDataProperty(capability, 'step_up_required') === false
  );
}

/**
 * Accepts inert JSON-decoded governance data; the Support store first removes
 * Vue's trusted reactive wrapper with `toRaw`. Authority is derived only from
 * an immutable descriptor snapshot, so accessors and throwing or observably
 * mutating reflection traps fail closed without property reads. JavaScript
 * cannot distinguish an inert transparent Proxy exposing the identical data
 * shape, and this helper does not claim to: arbitrary in-process Proxy identity
 * is not an authorization boundary. The backend remains authoritative.
 */
export function hasSupportAIReleaseRequestAccess(value: unknown): boolean {
  const snapshot = snapshotDataGraph(value);
  if (
    snapshot === INVALID_DATA_SNAPSHOT ||
    !isSafeRecord(snapshot) ||
    ownDataProperty(snapshot, 'available') !== true
  ) {
    return false;
  }
  const capabilities = ownDataProperty(snapshot, 'capabilities');
  if (!isSafeRecord(capabilities)) return false;
  const capability = ownDataProperty(capabilities, SUPPORT_AI_RELEASE_REQUEST);
  return (
    isSafeRecord(capability) &&
    ownDataProperty(capability, 'granted') === true &&
    ownDataProperty(capability, 'risk_level') === 'dangerous' &&
    ownDataProperty(capability, 'step_up_required') === true
  );
}

export function hasSupportAIReplayExecutionAccess(value: unknown): boolean {
  if (!isRecord(value) || value.available !== true || !isRecord(value.capabilities)) return false;

  const capability = value.capabilities[SUPPORT_AI_REPLAY_EXECUTION];
  return (
    isRecord(capability) &&
    capability.granted === true &&
    capability.risk_level === 'write' &&
    capability.step_up_required === false
  );
}

export function hasSupportAICustomerReadinessAccess(value: unknown): boolean {
  if (!isSafeRecord(value) || ownDataProperty(value, 'available') !== true) return false;
  const capabilities = ownDataProperty(value, 'capabilities');
  if (!isSafeRecord(capabilities)) return false;
  const capability = ownDataProperty(capabilities, SUPPORT_AI_CUSTOMER_READINESS);
  return (
    isSafeRecord(capability) &&
    ownDataProperty(capability, 'granted') === true &&
    ownDataProperty(capability, 'risk_level') === 'dangerous' &&
    ownDataProperty(capability, 'step_up_required') === true
  );
}

export function hasSupportAIRolloutControlAccess(
  context: Api.Support.AIGovernanceCapabilityContext | null | undefined
): boolean {
  if (!isSafeRecord(context) || ownDataProperty(context, 'available') !== true) return false;
  const capabilities = ownDataProperty(context, 'capabilities');
  if (!isSafeRecord(capabilities)) return false;
  const capability = ownDataProperty(capabilities, SUPPORT_AI_ROLLOUT_CONTROL);
  return (
    isSafeRecord(capability) &&
    ownDataProperty(capability, 'granted') === true &&
    ownDataProperty(capability, 'risk_level') === 'dangerous' &&
    ownDataProperty(capability, 'step_up_required') === true
  );
}

export function hasSupportAICalibrationReviewAccess(value: unknown): boolean {
  return hasCapability(value, SUPPORT_AI_CALIBRATION_REVIEW, 'write', false);
}

export function hasSupportAICalibrationManageAccess(value: unknown): boolean {
  return hasCapability(value, SUPPORT_AI_CALIBRATION_MANAGE, 'dangerous', true);
}

export function hasSupportAICalibrationApproveAccess(value: unknown): boolean {
  return hasCapability(value, SUPPORT_AI_CALIBRATION_APPROVE, 'dangerous', true);
}
